import { ChangeEvent, memo } from "react"

import { CellProps, useFocusManagement } from "components/TreeState"
import { ChangedPlanRecord, NumericWithUnit, PlanRecordType } from "../types"
import { checkBuyingStatus } from "utils/type-guards"
import { TextField, Tooltip } from "@material-ui/core"
import { formatFloatingPointNumber, parseNumber } from "utils/number-format"

import { Autocomplete } from "@material-ui/lab"
import { Bulky } from "./Bulky"
import { CellError } from "./CellError"
import { FieldsKey } from "../components/AutoCompute/types"
import { Info } from "./Info"
import Input from "ui/inputs/Input"
import { css } from "@emotion/css"
import { get } from "lodash"
import { isSemanticReadonly } from "../utils"
import { set } from "lodash/fp"
import { tableAggregations } from "../components/AutoCompute/useAggregations"
import { useAtomValue } from "jotai/utils"
import { useAutoCompute } from "../components/AutoCompute/useAutoCompute"
import { useAutocomputeValidation } from "../useAutocomputeValidation"
import { useBillingTypesRules } from "../useBillingTypeRules"
import { useSnapshot } from "valtio"
import { useTableSource } from "../TableStateProvider"
import { DEFAULT_PRECISION, usePrecision } from "providers/A12n"

const BENCHMARK_OPTIONS = ["CTR", "VTR", "ER", "LTR", "CR", "Reach", "IR"]

const UNIT_OPTIONS = [
  "Impressions",
  "Clicks",
  "View",
  "Engagement",
  "Listen",
  "Lead",
  "Acquisition",
  "App-install",
  "Reach",
]

export const PreciseMetricCell = memo(function _PreciseMetricCell(
  props: CellProps,
) {
  const precision = usePrecision()
  return <MetricCell {...props} precision={precision} />
})

export function MetricCell(props: CellProps) {
  const {
    rowId: id,
    dataKey,
    columnId,
    primaryKey,
    semantic,
    disabled,
    precision = DEFAULT_PRECISION,
  } = props
  const isBenchmark = semantic === "benchmarkRatio"
  const autoCompute = useAutoCompute()
  const tableSource = useTableSource()
  const aggregations = useAtomValue(tableAggregations)
  const readFrom = useSnapshot(tableSource.state)
  const readError = useSnapshot(tableSource.errors)
  const writeTo = tableSource.state

  const type = readFrom[id]?.type as PlanRecordType
  const packaged = readFrom[id]?.packaged
  const isMediaSubmedia =
    type === "media_type_plan" || type === "media_subtype_plan"

  const readonly =
    isSemanticReadonly(type, primaryKey as FieldsKey, semantic, packaged) ||
    disabled

  const buyingStatusVal = String(get(readFrom, `${id}.buyingStatus`))
  const buyingStatus = checkBuyingStatus(buyingStatusVal) ? buyingStatusVal : ""

  const action = readFrom[id]?.action
  const updatable = !action || action === "UPDATE_ORDER"

  const isBooking = primaryKey === "buyingMediaFields"
  const error = get(readError, `${id}.${columnId}`)
  const aggregatedValue = get(aggregations, [id, columnId]) ?? ""
  const hasAggregation = !!Object.keys(aggregations).includes(id)
  const { value = "", unit = "" } = (get(readFrom, `${id}.${dataKey}`) ||
    {}) as NumericWithUnit
  const appliedValue =
    isMediaSubmedia && hasAggregation && !isBooking && !packaged
      ? (aggregatedValue ?? value)
      : value

  const formattedValue = formatFloatingPointNumber(precision, 0)(appliedValue)

  const { getInputProps } = useFocusManagement()
  const ip = getInputProps()

  const { handleFocus: autoFocus, hasChanged } = useAutocomputeValidation(
    formattedValue,
    true,
  )

  const isPercentage = isBenchmark && unit !== "Reach"

  // apply billing type rules
  const viewable = useBillingTypesRules(
    readFrom[id] as ChangedPlanRecord,
    primaryKey,
    semantic,
  )
  if (!viewable || isMediaSubmedia) return <span />

  const options = isBenchmark ? BENCHMARK_OPTIONS : UNIT_OPTIONS

  const handleUnitChange = (
    _event: ChangeEvent<{}>,
    selected: string | null,
  ) => {
    if (!selected) {
      writeTo[id] = set(`${dataKey}`, undefined, writeTo[id])
    } else {
      writeTo[id] = set(`${dataKey}.unit`, selected, writeTo[id])
    }
    updatable && (writeTo[id] = set("action", "UPDATE", writeTo[id]))
  }

  const handleValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!unit) return
    const updated = parseNumber(event.target.value ?? 0)
    if (updated) {
      writeTo[id] = set(`${dataKey}.value`, updated, writeTo[id])
    } else {
      writeTo[id] = set(`${dataKey}`, undefined, writeTo[id])
    }
    updatable && (writeTo[id] = set("action", "UPDATE", writeTo[id]))
  }

  const handleBlur = () => {
    hasChanged(formattedValue) &&
      autoCompute?.compute(writeTo[id] as ChangedPlanRecord)
  }

  const isDisabled = buyingStatus === "POSTED_FOR_BILLING"

  return (
    <>
      <Bulky {...props} otherDisabled={!!readonly}>
        <div className={styles.container} onBlur={handleBlur}>
          <div className={styles.unitInput}>
            <Tooltip title={unit || ""} placement="bottom-start">
              <Autocomplete
                disabled={readonly || isDisabled}
                classes={{
                  popper: styles.popper,
                  option: styles.option,
                  endAdornment: readonly ? styles.hidden : undefined,
                  inputRoot: styles.autocompleteInput,
                  root: styles.unitInputBox,
                }}
                getOptionLabel={(opt) => opt}
                value={unit || null}
                onChange={handleUnitChange}
                options={options || []}
                openOnFocus={false}
                onKeyDown={ip.onKeyDown}
                filterSelectedOptions
                getOptionSelected={(opt, val) => opt === val}
                popupIcon={""}
                className={styles.auto}
                disableClearable={!unit || readonly}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    onFocus={ip.onFocus}
                    onBlur={(e) => {
                      e.stopPropagation()
                      ip.onBlur(e)
                    }}
                    fullWidth
                    name={dataKey}
                    variant="standard"
                    placeholder={!readonly ? "Select unit" : ""}
                    InputProps={{
                      disableUnderline: true,
                      classes: {
                        root: styles.inputRoot,
                      },
                      ...params.InputProps,
                    }}
                  />
                )}
              />
            </Tooltip>
          </div>
          <div className={styles.valueInput}>
            <Input
              readOnly={readonly}
              disabled={isDisabled}
              onBlur={ip.onBlur}
              onFocus={autoFocus}
              value={formattedValue}
              style={{ textAlign: "end" }}
              autoComplete="off"
              type="tel"
              className={styles.input}
              onChange={handleValueChange}
              placeholder={!readonly ? "value" : ""}
            />
            {isPercentage && !!formattedValue ? <span>%</span> : null}
          </div>
        </div>
      </Bulky>
      <Info {...props} />
      {!!error ? <CellError error={error} /> : null}
    </>
  )
}
const styles = {
  input: css`
    text-align: end;
  `,
  title: css`
    min-width: 64px;
  `,
  unit: css`
    text-align: end;
    width: 100%;
  `,
  container: css`
    width: 100%;
    display: grid;
    grid-auto-flow: column;
    grid-template-columns: 1fr 1fr;
    align-items: center;
  `,
  unitInput: css`
    display: flex;
    justify-content: start;
    align-items: center;
    width: 100%;
    font-size: 11px;
  `,
  valueInput: css`
    display: flex;
  `,
  popper: css`
    width: max-content;
    min-width: max-content;
  `,
  option: css`
    font-size: 13px;
    white-space: nowrap;
  `,
  hidden: css`
    display: none;
  `,
  autocompleteInput: css`
    min-height: var(--spacing-m);
  `,
  auto: css`
    width: 100%;
    padding: 0;
    .icon {
      opacity: 0;
    }
    :hover {
      .icon {
        opacity: 1;
      }
    }
  `,
  inputRoot: css`
    font-size: 11px;
    font-weight: inherit;
    padding: 0;
    padding-right: 0;
    align-items: center;
    &.Mui-disabled {
      color: inherit;
      padding: 0;
    }
  `,
  unitInputBox: css`
    &.MuiAutocomplete-hasPopupIcon {
      &.MuiAutocomplete-hasClearIcon {
        .MuiAutocomplete-inputRoot {
          padding: 0;
        }
      }
    }
  `,
}
